import { FiDialogsService } from '@fi-shared/fi-dialogs';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Inject,
    Injector,
    Input,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FiEditNamesMatComponent } from '@fi-app/components/fi-edit-names-mat/fi-edit-names-mat.component';
import { ChecklistService } from '../../checklist.service';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { AppTextService } from '@fi-shared/app-text';
import { ControlContainer, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgForm, NgModel } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { ChecklistModel } from '@fi-app/checklist/checklist.model';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';

@Component({
    selector: 'fi-checklist-edit-general',
    templateUrl: './general.component.html',
    styleUrls: ['./general.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checklist-edit-general' },
    viewProviders: [
        {
            provide: ControlContainer,
            useExisting: NgForm,
        },
    ],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiChecklistEditGeneralComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiChecklistEditGeneralComponent,
        },
    ],
    standalone: false,
})
export class FiChecklistEditGeneralComponent
    extends ControlValueAccessorValidatorBase<ChecklistModel>
    implements OnInit
{
    @Input() checklist: ChecklistModel;
    @Input() selectedLanguage;
    @ViewChild(FiEditNamesMatComponent, { static: false }) editNamesMat: FiEditNamesMatComponent;
    @Output() templateChanged = new EventEmitter();

    isNameRequiredFn: () => () => boolean;

    get nameModelList(): NgModel[] {
        return this.editNamesMat ? this.editNamesMat.nameModelList.toArray() : [];
    }

    constructor(
        @Inject('defaultLanguageId') private defaultLanguageId: number,
        private injector: Injector,
        private checklistService: ChecklistService,
        private loadingService: LoadingService,
        private fiDialogsService: FiDialogsService,
        private appTextService: AppTextService,
        private form: NgForm,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.isNameRequiredFn = this.isNameRequired.bind(this);
        this.loadTemplates().then(() => this.filterTemplates());
        this.checklist = this.ngModel;
    }

    isNameRequired(contentName) {
        return !this.checklist.Names.some((name: IContentLanguageLabelModel) => name.Value);
    }

    isDefaultLanguage(languageId) {
        return languageId === this.defaultLanguageId;
    }

    isDefaultContentLabel(contentLabel) {
        return this.isDefaultLanguage(contentLabel.ContentLanguageId);
    }

    onNameChange() {
        setTimeout(() => {
            this.doPropagateChanges();
        });
    }

    editNamesAfterViewInit() {
        this.doPropagateChanges();
    }

    templates: any[] = [];
    templatesFiltered: any[] = [];
    templatesSearchText: string = '';
    selectedTemplateId: number;
    selectedTemplateIdPrev: number;

    loadTemplates() {
        return this.checklistService.GetLightWeightCheckListTemplates().then((result) => {
            this.templates = result;
        });
    }

    filterTemplates() {
        this.templatesFiltered = this.templates.filter(
            (template) =>
                !this.templatesSearchText ||
                template.Name.toLowerCase().indexOf(this.templatesSearchText.toLowerCase()) > -1,
        );
    }

    templateItemClick(id) {
        if (this.selectedTemplateId === id) {
            this.selectedTemplateId = null;
        } else {
            this.selectedTemplateId = id;
        }
        this.checklistTemplateOnChange();
    }

    checklistTemplateOnChange() {
        this.checklistTemplateOnChangeAskConfirm().then((result) => {
            if (result) {
                this.selectedTemplateIdPrev = this.selectedTemplateId;
                this.getChecklistTemplateByChangedId(this.selectedTemplateId).then((checklistTemplate) => {
                    this.checklist.initFromTemplate(checklistTemplate, this.injector);
                    this.form.control.markAsPristine();
                    this.templateChanged.emit();
                });
            } else {
                this.selectedTemplateId = this.selectedTemplateIdPrev;
            }
        });
    }

    checklistTemplateOnChangeAskConfirm(): Promise<boolean> {
        if (this.form.pristine) {
            return Promise.resolve(true);
        }
        return new Promise((resolve) => {
            this.fiDialogsService
                .confirm({
                    headerText: this.appTextService.get('checklist.change_template_confirm_header'),
                    messageText: this.appTextService.get('checklist.change_template_confirm'),
                })
                .onYesButtonClick(() => {
                    resolve(true);
                })
                .onNoButtonClick(() => {
                    resolve(false);
                });
        });
    }

    getChecklistTemplateByChangedId(templateId): Promise<any> {
        return !templateId
            ? Promise.resolve({ CheckPoints: [{}] })
            : this.loadingService.trackPromise(this.checklistService.getCheckListTemplate(templateId));
    }

    getChecklistDescriptionPlaceholder(contentDescription) {
        if (this.isDefaultContentLabel(contentDescription))
            return this.appTextService.get('checklist.checklist_description');

        // find description value in default language and use it as placeholder for not defaults ones
        const divElement: HTMLDivElement = document.createElement('div');
        for (let i = 0; i < this.checklist.Descriptions.length; i++) {
            divElement.innerHTML = this.checklist.Descriptions[i].Value;
            if (this.isDefaultContentLabel(this.checklist.Descriptions[i]))
                return this.checklist.Descriptions[i].Value
                    ? divElement.innerText
                    : this.appTextService.get('checklist.checklist_description');
        }
    }

    onDescriptionChange() {
        this.doPropagateChanges();
    }
}
